import { checkInvitationPassword, roleLabel } from "@xop-any-time/shared";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent, type JSX } from "react";

import { ApiError } from "../../lib/api.js";
import { PageHeader } from "../portal/crm.js";
import { accountKeys, changePassword, getProfile, updateProfile } from "./api.js";

/**
 * Your profile — who you are, what you may do, and where your company sits.
 *
 * ASSEMBLED FROM TWO BOXES, which is why some fields are editable here and
 * others are not, and the page says so rather than leaving a disabled input to
 * be read as a bug:
 *
 *   • portal-api owns the HUMAN — name, password, second factor. Editable.
 *   • crm-api owns the AUTHORISATION — which reseller, what role. Read-only
 *     here by design; XOP and your own owners decide it.
 *
 * THE EMAIL IS THE INTERESTING ONE. It looks like an ordinary profile field
 * and is not: it is the key crm-api resolves your reseller membership on, so
 * changing it here would detach your access from your identity — the account
 * would keep working until the next token refresh and then start refusing you
 * with nothing on either box to explain why. The field says that in one line,
 * because a disabled box with no reason invites a support ticket.
 */
export function ProfilePage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: accountKeys.profile,
    queryFn: getProfile,
  });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  // Seed the form once the profile arrives. Keyed on the loaded values rather
  // than a mount effect so a background refetch does not stomp on typing.
  useEffect(() => {
    if (!data) return;
    setFirstName(data.profile.firstName ?? "");
    setLastName(data.profile.lastName ?? "");
  }, [data?.profile.firstName, data?.profile.lastName]);

  const saveName = useMutation({
    mutationFn: () => updateProfile({ firstName, lastName }),
    onSuccess: () => {
      setToast("Your name is updated.");
      void qc.invalidateQueries({ queryKey: accountKeys.profile });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Your profile" />
        <Alert severity="error">
          We could not load your profile just now. Refresh the page to try again.
        </Alert>
      </Box>
    );
  }

  const p = data.profile;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Your profile"
        description="Your sign-in details, and the company you act for."
      />

      {/* ── Who you are ─────────────────────────────────────────────── */}
      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Typography sx={{ fontWeight: 600 }}>Your details</Typography>

          <Box
            component="form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              saveName.mutate();
            }}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                inputProps={{ maxLength: 80 }}
                sx={{ flex: "1 1 200px" }}
              />
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                inputProps={{ maxLength: 80 }}
                sx={{ flex: "1 1 200px" }}
              />
            </Box>

            <TextField
              label="Email"
              value={p.email}
              disabled
              /* The reason, not just the disabled state. This address is what
                 links your sign-in to your reseller access. */
              helperText="Your email identifies your access to XOP Networks, so it cannot be changed here. Ask your XOP Networks channel manager."
              data-testid="profile-email"
            />

            {saveName.isError && (
              <Alert severity="error">
                {saveName.error instanceof ApiError
                  ? saveName.error.message
                  : "We could not save that."}
              </Alert>
            )}

            <Box>
              <Button type="submit" variant="contained" disabled={saveName.isPending}>
                {saveName.isPending ? "Saving…" : "Save"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── What you may do ─────────────────────────────────────────── */}
      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography sx={{ fontWeight: 600 }}>Your access</Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Chip
              label={roleLabel(p.role, p.roleLabel)}
              color="primary"
              variant="outlined"
              size="small"
              data-testid="profile-role"
            />
            <Typography sx={{ color: "text.secondary" }}>at</Typography>
            <Typography sx={{ fontWeight: 500 }} data-testid="profile-company">
              {p.resellerName}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {p.role === "owner"
              ? "You can manage your team's access and add sub-resellers beneath your company."
              : "You can see everything your company sells. Ask an owner at your company to change your access."}
          </Typography>

          {p.twoFactorEnabled !== null && (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Two-factor sign-in is {p.twoFactorEnabled ? "on" : "off"} for this account.
            </Typography>
          )}
        </CardContent>
      </Card>

      <PasswordCard email={p.email} onDone={() => setToast("Your password is changed.")} />

      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ""}
      />
    </Box>
  );
}

/**
 * The password form.
 *
 * THE RULE IS CHECKED HERE WITH THE SAME FUNCTION THE SERVER USES —
 * `checkInvitationPassword` from `@xop-any-time/shared`, which also backs the
 * accept-invitation screen and the BFF route that enforces it. The client copy
 * exists to give an answer without a round trip; it is NOT the enforcement,
 * and because it is literally the same function it cannot drift into promising
 * something the server will refuse.
 */
function PasswordCard({ email, onDone }: { email: string; onDone: () => void }): JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const verdict = next === "" ? null : checkInvitationPassword(next, email);
  const mismatch = confirm !== "" && confirm !== next;

  const save = useMutation({
    mutationFn: () => changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      onDone();
    },
  });

  const blocked = current === "" || next === "" || mismatch || verdict?.ok === false;

  return (
    <Card>
      <CardContent
        component="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!blocked) save.mutate();
        }}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Typography sx={{ fontWeight: 600 }}>Change your password</Typography>

        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          /* Required, and not as a formality: it is what stops a stolen
             session from quietly taking the account over. */
          helperText="We ask for this so a stolen session cannot change your password."
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          error={verdict?.ok === false}
          helperText={
            verdict?.ok === false
              ? verdict.message
              : "Length matters more than symbols. A short phrase works well."
          }
        />
        <TextField
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch}
          helperText={mismatch ? "These do not match." : " "}
        />

        {save.isError && (
          <Alert severity="error">
            {save.error instanceof ApiError ? save.error.message : "We could not change it."}
          </Alert>
        )}

        <Box>
          <Button type="submit" variant="contained" disabled={blocked || save.isPending}>
            {save.isPending ? "Changing…" : "Change password"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
